import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ListChecks, ChevronDown, ChevronUp, Eye, ShieldCheck, ShieldAlert, Play, X } from 'lucide-react'

const riskStyles = {
  read:      { color: 'text-emerald-400', bg: 'bg-emerald-900/30', border: 'border-emerald-700/40', key: 'read',   icon: Eye },
  write:     { color: 'text-amber-400',   bg: 'bg-amber-900/30',   border: 'border-amber-700/40',   key: 'write',  icon: ShieldCheck },
  dangerous: { color: 'text-red-400',     bg: 'bg-red-900/30',     border: 'border-red-700/40',     key: 'danger', icon: ShieldAlert },
}

function RiskBadge({ risk }) {
  const { t } = useTranslation()
  const s = riskStyles[risk] || riskStyles.write
  const Icon = s.icon
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${s.bg} ${s.color} border ${s.border} shrink-0`}>
      <Icon className="h-2.5 w-2.5" />
      {t(`terminalAi.risk.${s.key}`)}
    </span>
  )
}

/**
 * Planner de execução — aparece entre os messages e o input quando o agente
 * planeja executar múltiplos comandos automaticamente.
 * Requer aprovação explícita do usuário antes de iniciar.
 */
export default function TerminalAIExecutionPlan({ plan, onApprove, onCancel }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(true)

  const dangerCount  = plan.commands.filter(c => c.riskLevel === 'dangerous').length
  const writeCount   = plan.commands.filter(c => c.riskLevel === 'write').length
  const readCount    = plan.commands.filter(c => c.riskLevel === 'read').length
  const hasDanger    = dangerCount > 0

  return (
    <div className={`border-t ${hasDanger ? 'border-red-700/50 bg-red-950/20' : 'border-theme-secondary/40 bg-theme-secondary/10'}`}>
      {/* Header do planner */}
      <div
        className="flex items-center justify-between px-4 py-2 cursor-pointer"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-2">
          <ListChecks className={`h-4 w-4 ${hasDanger ? 'text-red-400' : 'text-theme-secondary'}`} />
          <span className={`text-xs font-semibold ${hasDanger ? 'text-red-300' : 'text-theme-secondary'}`}>
            {t('terminalAi.plan.title')}
          </span>
          {/* Contadores por tipo */}
          <div className="flex items-center gap-1">
            {readCount > 0 && (
              <span className="rounded-full bg-emerald-900/40 border border-emerald-700/40 px-1.5 py-0.5 text-[10px] font-medium text-emerald-400">
                {t('terminalAi.plan.countRead', { count: readCount })}
              </span>
            )}
            {writeCount > 0 && (
              <span className="rounded-full bg-amber-900/40 border border-amber-700/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-400">
                {t('terminalAi.plan.countWrite', { count: writeCount })}
              </span>
            )}
            {dangerCount > 0 && (
              <span className="rounded-full bg-red-900/40 border border-red-700/40 px-1.5 py-0.5 text-[10px] font-medium text-red-400">
                {t('terminalAi.plan.countDanger', { count: dangerCount })}
              </span>
            )}
          </div>
        </div>
        <button className="text-slate-500 hover:text-slate-300">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-3 space-y-3">
          {/* Resumo da intenção do agente */}
          {plan.message && (
            <p className="text-xs text-slate-400 border-l-2 border-slate-600 pl-2 italic">
              {plan.message.length > 180 ? plan.message.slice(0, 180) + '…' : plan.message}
            </p>
          )}

          {/* Lista de comandos */}
          <div className="space-y-1.5 max-h-48 overflow-y-auto terminal-scroll pr-1">
            {plan.commands.map((cmd, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-[10px] text-slate-600 font-mono mt-1 w-4 shrink-0 text-right">
                  {i + 1}.
                </span>
                <code className="flex-1 text-[11px] font-mono text-slate-300 bg-slate-900/60 rounded px-2 py-1 break-all leading-relaxed">
                  {cmd.command}
                </code>
                <div className="mt-0.5">
                  <RiskBadge risk={cmd.riskLevel} />
                </div>
              </div>
            ))}
          </div>

          {hasDanger && (
            <p className="text-[11px] text-red-400 bg-red-900/20 border border-red-700/30 rounded px-2 py-1.5">
              {t('terminalAi.plan.dangerWarning')}
            </p>
          )}

          {/* Ações */}
          <div className="flex items-center justify-between pt-1">
            <p className="text-[10px] text-slate-500">
              {t('terminalAi.plan.sequenceHint', { count: plan.commands.length })}
            </p>
            <div className="flex gap-2">
              <button
                onClick={onCancel}
                className="inline-flex items-center gap-1 rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-slate-700"
              >
                <X className="h-3 w-3" />
                {t('common.buttons.cancel')}
              </button>
              <button
                onClick={onApprove}
                className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-white transition ${
                  hasDanger
                    ? 'bg-red-700 hover:bg-red-600'
                    : 'bg-theme-secondary hover:bg-theme-secondary/90'
                }`}
              >
                <Play className="h-3 w-3" />
                {t('terminalAi.plan.approve')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
